/**
 * Customer Autocomplete Hook
 * 
 * Provides autocomplete functionality for customer search using Elasticsearch.
 * Supports debounced search with minimum 2 characters and popular items on empty query.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { v1Api } from '../../../api/generatedClient'
import { getApiBaseUrl } from '../../../api/authTokens'
import type { APICustomerAutocompleteResponse } from '../../../generated-types/models/APICustomerAutocompleteResponse'
import type { V1SearchCustomersCustomersAutocompleteRetrieveRequest } from '../../../generated-types/apis/V1Api'

interface CustomerAutocompleteResponse {
  suggestions: Array<{
    id: string
    name: string
    self: string
  }>
  total: number
}

/**
 * Fetches customer autocomplete suggestions
 */
const fetchCustomerAutocomplete = async (
  query: string,
  size = 10
): Promise<CustomerAutocompleteResponse> => {
  const response: APICustomerAutocompleteResponse = await v1Api.v1SearchCustomersCustomersAutocompleteRetrieve({
    q: query || undefined,
    size,
  } as V1SearchCustomersCustomersAutocompleteRetrieveRequest)

  const baseUrl = getApiBaseUrl()

  return {
    suggestions: response.suggestions.map(suggestion => ({
      id: suggestion.id,
      name: suggestion.name,
      self: `${baseUrl}v1/customers/customers/${suggestion.id}/`,
    })),
    total: response.total,
  }
}

/**
 * Hook for customer autocomplete
 * 
 * @param options - Configuration options
 * @returns Autocomplete hook result with suggestions and loading state
 */
export function useCustomerAutocomplete(
  options: {
    size?: number
    enabled?: boolean
  } = {}
) {
  const { size = 10, enabled = true } = options
  const [query, setQuery] = useState('')
  
  const debouncedQuery = useDebouncedValue(query, 300)

  const { data, isLoading, error } = useQuery<CustomerAutocompleteResponse, Error>({
    queryKey: ['customer-autocomplete', debouncedQuery],
    queryFn: () => fetchCustomerAutocomplete(debouncedQuery, size),
    enabled: enabled && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    suggestions: data?.suggestions ?? [],
    total: data?.total ?? 0,
    isLoading: isLoading && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    error,
    updateQuery: setQuery,
    query,
  }
}


